import { PROJECT_STATUS_LABELS, type UnsavedTimer } from '@hygge/shared';

/**
 * The sentence shown when a stop crossed the monthly cutoff and the entry was
 * not written. The server already stopped the timer and named the loss in its
 * 200 — this only puts that answer on screen, because a success response
 * carries no global toast and a silent loss is HW-837's bug all over again.
 * Named the same way the refusal texts of HW-846 are: the month, the exact
 * amount, and the way to get it back. [HW-861]
 *
 * The other reason is a project archived or put on hold while the timer ran:
 * named by the project and its state, the same way. [HW-935]
 */
export function unsavedTimerMessage(unsaved: UnsavedTimer): string {
  const amount = formatUnsavedDuration(unsaved.durationSeconds);
  if ('month' in unsaved) {
    return (
      `${amount} from ${unsaved.month} was not saved — ` +
      'that month is closed. Ask your project manager or HR to add it.'
    );
  }
  const state = (PROJECT_STATUS_LABELS[unsaved.projectStatus] ?? unsaved.projectStatus).toLowerCase();
  return (
    `${amount} on ${unsaved.project} was not saved — ` +
    `the project is ${state}. If these hours belong there, ask your project manager ` +
    'to make the project active again and add them.'
  );
}

/**
 * "3h 07m" / "42m" — the shape the timer's own activity log uses — with one
 * extra case: a sub-minute stop reads "less than a minute" rather than "0m",
 * because the message exists to name a loss and "0m" reads as no loss at all.
 */
export function formatUnsavedDuration(durationSeconds: number): string {
  const total = Math.max(0, Math.round(durationSeconds));
  if (total < 60) return 'less than a minute';
  const hours = Math.floor(total / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const paddedMins = hours > 0 ? String(mins).padStart(2, '0') : String(mins);
  return hours > 0 ? `${hours}h ${paddedMins}m` : `${mins}m`;
}
